import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { 
  $getSelection, 
  $isRangeSelection, 
  FORMAT_TEXT_COMMAND, 
  $createParagraphNode,
  $createTextNode,
  $getRoot
} from "lexical";
import { $setBlocksType } from "@lexical/selection";
import { $createHeadingNode } from "@lexical/rich-text";
import { $createListNode } from "@lexical/list";
import { TOGGLE_LINK_COMMAND, $isLinkNode } from "@lexical/link";
import { useEffect, useState, useCallback, useRef } from "react";
import { 
  Bold, 
  Italic, 
  Underline, 
  Strikethrough, 
  Code, 
  Link, 
  Sparkles, 
  Wand2,
  ChevronDown,
  MoreHorizontal,
  Sigma,
  Unlink,
  Loader2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-stream`;

const TEXT_TYPES = [
  { label: "Text", command: "paragraph" },
  { label: "Heading 1", command: "h1" },
  { label: "Heading 2", command: "h2" },
  { label: "Heading 3", command: "h3" },
  { label: "Bulleted List", command: "bullet" },
  { label: "Numbered List", command: "number" },
];

function ToolbarButton({ 
  onClick, 
  children, 
  className,
  title,
  disabled
}: { 
  onClick: (e: React.MouseEvent) => void; 
  children: React.ReactNode;
  className?: string;
  title?: string;
  disabled?: boolean;
}) {
  return (
    <button
      onMouseDown={(e) => {
        e.preventDefault();
        if (!disabled) onClick(e);
      }}
      className={cn(
        "flex items-center justify-center rounded-md p-1.5 hover:bg-muted/80 transition-colors",
        "text-muted-foreground hover:text-foreground",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      type="button"
      title={title}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

function ToolbarIconButton({ 
  onClick, 
  children,
  title,
  active
}: { 
  onClick: (e: React.MouseEvent) => void; 
  children: React.ReactNode;
  title?: string;
  active?: boolean;
}) {
  return (
    <button
      onMouseDown={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className={cn(
        "flex items-center justify-center w-7 h-7 rounded-md hover:bg-muted/80 transition-colors",
        active ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
      )}
      type="button"
      title={title}
    >
      {children}
    </button>
  );
}

export function FloatingToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  });
  const [currentBlockType, setCurrentBlockType] = useState("paragraph");
  const [isLinkInputVisible, setIsLinkInputVisible] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [hasLink, setHasLink] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const toolbarRef = useRef<HTMLDivElement>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);

  const applyBlockType = useCallback((command: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        switch (command) {
          case "h1":
            $setBlocksType(selection, () => $createHeadingNode("h1"));
            break;
          case "h2":
            $setBlocksType(selection, () => $createHeadingNode("h2"));
            break;
          case "h3":
            $setBlocksType(selection, () => $createHeadingNode("h3"));
            break;
          case "bullet":
            $setBlocksType(selection, () => $createListNode("bullet"));
            break;
          case "number":
            $setBlocksType(selection, () => $createListNode("number"));
            break;
          case "paragraph":
            $setBlocksType(selection, () => $createParagraphNode());
            break;
        }
        setCurrentBlockType(command);
      }
    });
  }, [editor]);

  const insertLink = useCallback(() => {
    if (!linkUrl.trim()) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    } else {
      const url = linkUrl.startsWith("http://") || linkUrl.startsWith("https://") 
        ? linkUrl 
        : `https://${linkUrl}`;
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
    }
    setIsLinkInputVisible(false);
    setLinkUrl("");
  }, [editor, linkUrl]);

  const removeLink = useCallback(() => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    setHasLink(false);
    setIsLinkInputVisible(false);
    setLinkUrl("");
  }, [editor]);

  const improveWriting = useCallback(async () => {
    if (!selectedText.trim() || isImproving) return;
    
    setIsImproving(true);
    
    try {
      const response = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: [
            {
              role: "system",
              content: "You are a writing assistant. Improve the given text to be clearer, more professional, and better structured. Return ONLY the improved text without any explanations, quotes, or additional commentary."
            },
            {
              role: "user",
              content: `Improve this text:\n\n${selectedText}`
            }
          ]
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error("Failed to improve text");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let improvedText = "";
      let textBuffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        textBuffer += decoder.decode(value, { stream: true });
        
        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) improvedText += content;
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      // Replace selected text with improved text
      if (improvedText.trim()) {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertText(improvedText.trim());
          }
        });
        toast.success("Text improved!");
      }
    } catch (error) {
      console.error("Error improving text:", error);
      toast.error("Failed to improve text. Please try again.");
    } finally {
      setIsImproving(false);
    }
  }, [editor, selectedText, isImproving]);

  useEffect(() => {
    const update = () => {
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) && !selection.isCollapsed()) {
          const domSelection = window.getSelection();
          if (domSelection && domSelection.rangeCount > 0) {
            const domRange = domSelection.getRangeAt(0);
            const rect = domRange.getBoundingClientRect();
            setCoords({
              left: rect.left + rect.width / 2,
              top: rect.top + window.scrollY - 48,
            });
            setIsVisible(true);
            setSelectedText(selection.getTextContent());

            // Check if selection contains a link
            const node = selection.anchor.getNode();
            const parent = node.getParent();
            const isLink = $isLinkNode(parent) || $isLinkNode(node);
            setHasLink(isLink);
            
            if (isLink) {
              const linkNode = $isLinkNode(parent) ? parent : node;
              if ($isLinkNode(linkNode)) {
                setLinkUrl(linkNode.getURL());
              }
            }
          }
        } else if (!isLinkInputVisible) {
          setIsVisible(false);
        }
      });
    };

    const unregister = editor.registerUpdateListener(() => {
      update();
    });

    return () => {
      unregister();
    };
  }, [editor, isLinkInputVisible]);

  // Focus link input when it becomes visible
  useEffect(() => {
    if (isLinkInputVisible && linkInputRef.current) {
      setTimeout(() => linkInputRef.current?.focus(), 50);
    }
  }, [isLinkInputVisible]);

  if (!isVisible) return null;

  const currentTypeLabel = TEXT_TYPES.find(t => t.command === currentBlockType)?.label || "Text";

  return (
    <div
      ref={toolbarRef}
      className="fixed z-[9999] flex flex-col items-start rounded-lg border border-border bg-popover shadow-lg"
      style={{ top: coords.top, left: coords.left, transform: "translateX(-50%)" }}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="flex items-center h-9 px-1 gap-0.5">
        {/* Improve Writing */}
        <ToolbarButton 
          onClick={improveWriting}
          className="gap-1.5 px-2 text-primary"
          title="Improve writing"
          disabled={isImproving}
        >
          {isImproving ? (
            <Loader2 size={14} className="text-primary animate-spin" />
          ) : (
            <Sparkles size={14} className="text-primary" />
          )}
          <span className="text-xs font-medium">Improve</span>
        </ToolbarButton>

        {/* Ask AI */}
        <ToolbarButton 
          onClick={() => {/* AI ask functionality */}}
          className="gap-1.5 px-2"
          title="Ask AI"
        >
          <Wand2 size={14} />
          <span className="text-xs font-medium">Ask AI</span>
        </ToolbarButton>

        <Separator orientation="vertical" className="h-5 mx-1" />

        {/* Text Type Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-1 px-2 h-7 rounded-md hover:bg-muted/80 transition-colors text-muted-foreground hover:text-foreground text-xs font-medium"
              type="button"
              onMouseDown={(e) => e.preventDefault()}
            >
              {currentTypeLabel}
              <ChevronDown size={12} className="text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[140px]">
            {TEXT_TYPES.map((type) => (
              <DropdownMenuItem
                key={type.command}
                onSelect={() => applyBlockType(type.command)}
                className={cn(
                  "text-sm",
                  currentBlockType === type.command && "bg-muted"
                )}
              >
                {type.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Separator orientation="vertical" className="h-5 mx-1" />

        {/* Formatting Buttons */}
        <ToolbarIconButton
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
          title="Bold"
        >
          <Bold size={14} />
        </ToolbarIconButton>

        <ToolbarIconButton
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
          title="Italic"
        >
          <Italic size={14} />
        </ToolbarIconButton>

        <ToolbarIconButton
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}
          title="Underline"
        >
          <Underline size={14} />
        </ToolbarIconButton>

        <ToolbarIconButton
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")}
          title="Strikethrough"
        >
          <Strikethrough size={14} />
        </ToolbarIconButton>

        <ToolbarIconButton
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code")}
          title="Mark as code"
        >
          <Code size={14} />
        </ToolbarIconButton>

        <ToolbarIconButton
          onClick={() => {/* Equation functionality */}}
          title="Mark as equation"
        >
          <Sigma size={14} />
        </ToolbarIconButton>

        {/* Link Button */}
        <button
          className={cn(
            "flex items-center gap-0.5 px-1.5 h-7 rounded-md hover:bg-muted/80 transition-colors",
            hasLink || isLinkInputVisible ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
          type="button"
          title="Add link"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsLinkInputVisible(!isLinkInputVisible);
          }}
        >
          <Link size={14} />
          <ChevronDown size={10} className="text-muted-foreground" />
        </button>

        <Separator orientation="vertical" className="h-5 mx-1" />

        {/* More Options */}
        <ToolbarIconButton
          onClick={() => {/* More options */}}
          title="More options"
        >
          <MoreHorizontal size={14} />
        </ToolbarIconButton>
      </div>

      {/* Link Input Panel */}
      {isLinkInputVisible && (
        <div 
          className="w-full border-t border-border p-2 space-y-2"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Link URL</label>
            <Input
              ref={linkInputRef}
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  insertLink();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setIsLinkInputVisible(false);
                }
              }}
              className="h-8 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              className="flex-1 h-8"
              onClick={(e) => {
                e.preventDefault();
                insertLink();
              }}
            >
              {hasLink ? "Update Link" : "Add Link"}
            </Button>
            {hasLink && (
              <Button 
                size="sm" 
                variant="outline"
                className="h-8"
                onClick={(e) => {
                  e.preventDefault();
                  removeLink();
                }}
              >
                <Unlink size={14} />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
